import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useLeaseCompStore, usePipedriveStore, usePopupStore, useViewSettingsStore } from "../..";
import useNavigateToLeaseComp from "../../hooks/useNavigateToLeaseComp";
import { leaseCompPropertyTypeToString, leaseCompSourceToString } from "../../utils/enumUtils";
import { formatDate, formatDateTime, formatNumber, formatPrice } from "../../utils/utils";
import FieldRow from "../FieldRow";
import ExpandIcon from "../icons/ExpandIcon";
import LeaseCompIcon from "../icons/LeaseCompIcon";
import MapPopupWithFlyTo from "../MapPopupWithFlyTo";
import popupStyles from "../Popup.module.css";

function LeaseCompPopup({ id }: ILeaseCompPopupProps) {
    const navigateToLeaseComp = useNavigateToLeaseComp();
    const leaseCompStore = useLeaseCompStore();
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const viewSettingsStore = useViewSettingsStore();
    const leaseComp = useMemo(() => computed(() => leaseCompStore.leaseComps.find(lc => lc.id === id)), [id]).get();
    const pipedriveOrganization = useMemo(() => computed(() => leaseComp && pipedriveStore.organizations.find(o => o.id === leaseComp.pipedriveCompanyId)), [leaseComp]).get();

    if (leaseCompStore.isLeaseCompsLoading.fetching)
        return null;

    if (!leaseComp)
        return null;

    return (
        <MapPopupWithFlyTo
            longitude={leaseComp.centerPoint.longitude}
            latitude={leaseComp.centerPoint.latitude}
            closeButton={true}
            closeOnClick={false}
            onClose={() => popupStore.close()}
            anchor={viewSettingsStore.isPopupExpanded ? "left" : "bottom"}
            offset={20}
        >
            <div className={popupStyles.header}>
                <LeaseCompIcon color="white" />
                <div className={popupStyles.titleClickable} onClick={() => navigateToLeaseComp(id, true)}>
                    <div>{pipedriveOrganization?.name}</div>
                    <div>{leaseComp.address}</div>
                    {!!leaseComp.signedDate && <span className={popupStyles.headerDescription}>SIGNED: {formatDate(leaseComp.signedDate)}</span>}
                </div>
            </div>
            <div className={popupStyles.table}>
                <div className={popupStyles.tableItem}>
                    <div>Land $/Acre</div>
                    <div>{!!leaseComp.landPsf && formatPrice(leaseComp.landPsf)}</div>
                </div>
                <div className={popupStyles.tableItem}>
                    <div>Building $/PSF</div>
                    <div>{!!leaseComp.buildingPsf && formatPrice(leaseComp.buildingPsf)}</div>
                </div>
                <div className={popupStyles.tableItem}>
                    <div>Monthly Rent</div>
                    <div>{!!leaseComp.totalRent && formatPrice(leaseComp.totalRent / 12)}</div>
                </div>
            </div>
            <div className={popupStyles.body}>
                <FieldRow label="LEASE EXP">
                    {!!leaseComp.leaseExpiration && formatDate(leaseComp.leaseExpiration)}
                </FieldRow>
                {viewSettingsStore.isPopupExpanded && (
                    <>
                        <FieldRow label="PARCEL SIZE">
                            {!!leaseComp.parcelSizeAcres && `${formatNumber(leaseComp.parcelSizeAcres)} Acres`}
                        </FieldRow>
                    </>
                )}
                <FieldRow label="TENANT LAND SIZE">
                    {!!leaseComp.tenantLandSize && `${formatNumber(leaseComp.tenantLandSize)} Acres`}
                </FieldRow>
                <FieldRow label="BUILDING SIZE">
                    {!!leaseComp.buildingSize && `${formatNumber(leaseComp.buildingSize)} Sq-Ft`}
                </FieldRow>
                <FieldRow label="OPTIONS">
                    {leaseComp.options}
                </FieldRow>
                <FieldRow label="ZONING">
                    {leaseComp.zoning}
                </FieldRow>
                {viewSettingsStore.isPopupExpanded && (
                    <>
                        <FieldRow label="ANNUAL RENT">
                            {!!leaseComp.totalRent && formatPrice(leaseComp.totalRent)}
                        </FieldRow>
                        <FieldRow label="PROPERTY TYPE">
                            {!!leaseComp.propertyType && leaseCompPropertyTypeToString(leaseComp.propertyType)}
                        </FieldRow>
                        <FieldRow label="COMP SOURCE">
                            {!!leaseComp.compSource && leaseCompSourceToString(leaseComp.compSource)}
                        </FieldRow>
                        <FieldRow label="NOTES">
                            {leaseComp.notes}
                        </FieldRow>
                    </>
                )}
                <div className={popupStyles.footer}>
                    <div className={popupStyles.modified}>
                        <span>{leaseComp.modifiedByUser}</span>
                        {!!leaseComp.modifiedDate && <><br /><span>{formatDateTime(leaseComp.modifiedDate)}</span></>}
                    </div>
                    <ExpandIcon />
                </div>
            </div>
        </MapPopupWithFlyTo>
    );
}

export default observer(LeaseCompPopup);

interface ILeaseCompPopupProps {
    id: number;
}
